import { ssrTrack } from '@/lib/actions/analytics/ssr-track';
import { getDisplayNumber, getSign } from '@/lib/numbers';
import {
    addCleanHeatAndAcAtom,
    addHeatPumpWaterHeaterAtom,
    addHvacUseAtom,
    addInductionStoveAtom,
    addPeopleAtom,
    basicInfoExpandAtom,
    evsAtom,
    numEvsAtom,
} from '@/ui/atoms/form-atoms';
import { useClearInputs } from '@/ui/hooks/useClearInputs';
import { useCustomerInputs } from '@/ui/hooks/useCustomerInputs';
import { useEnergyOffset } from '@/ui/hooks/useEnergyOffset';
import { useResponsiveUi } from '@/ui/hooks/useResponsiveUi';
import { Loader, Transition } from '@mantine/core';
import {
    IconChevronLeft,
    IconChevronRight,
    IconCircleMinus,
} from '@tabler/icons-react';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import styles from './LeftDrawer.module.css';

export const LeftDrawer: React.FC = () => {
    const { inputComplete, loading, usageResult, errorMessage } =
        useEnergyOffset();
    const { inputDict } = useCustomerInputs();

    const [leftDrawerExpand, setLeftDrawerExpand] = useState(false);

    // Avoid overlapping floating menus.
    const basicInfoExpand = useAtomValue(basicInfoExpandAtom);

    const { isMobile } = useResponsiveUi();

    // Close the left drawer when the basic info menu is open.
    useEffect(() => {
        if (leftDrawerExpand && basicInfoExpand) {
            setLeftDrawerExpand(false);
        }
    }, [basicInfoExpand, leftDrawerExpand, setLeftDrawerExpand]);

    // Form inputs.
    const [evs, setEvs] = useAtom(evsAtom);
    const [numEvs, setNumEvs] = useAtom(numEvsAtom);

    const [addPeople, setAddPeople] = useAtom(addPeopleAtom);
    const [addHvacUse, setAddHvacUse] = useAtom(addHvacUseAtom);
    const [addInductionStove, setAddInductionStove] = useAtom(
        addInductionStoveAtom
    );
    const [addCleanHeatAndAc, setAddCleanHeatAndAc] = useAtom(
        addCleanHeatAndAcAtom
    );
    const [addHeatpumpWaterHeater, setAddHeatpumpWaterHeater] = useAtom(
        addHeatPumpWaterHeaterAtom
    );

    const {
        clearEv,
        clearOtherScenarios,
        clearInductionStove,
        clearCleanHeatAndAc,
        clearHeatpumpWaterHeater,
        clearAll,
    } = useClearInputs();

    const validEvs = useMemo(() => {
        return evs.filter((ev) => ev.type && ev.milesPerYear && ev.homeChgPct);
    }, [evs]);

    const hasEv = useMemo(() => {
        return validEvs.length > 0;
    }, [validEvs.length]);

    const hasOtherScenarios = useMemo(() => {
        return Boolean(Number(addPeople) || addHvacUse);
    }, [addHvacUse, addPeople]);

    // Auto-expand drawer.
    useEffect(() => {
        if (!isMobile && hasEv) {
            setLeftDrawerExpand(true);
        }
    }, [hasEv, isMobile, setLeftDrawerExpand]);

    useEffect(() => {
        if (!isMobile && hasOtherScenarios) {
            setLeftDrawerExpand(true);
        }
    }, [hasOtherScenarios, isMobile, setLeftDrawerExpand]);

    useEffect(() => {
        if (!isMobile && addInductionStove) {
            setLeftDrawerExpand(true);
        }
    }, [addInductionStove, isMobile, setLeftDrawerExpand]);

    useEffect(() => {
        if (!isMobile && addCleanHeatAndAc) {
            setLeftDrawerExpand(true);
        }
    }, [addCleanHeatAndAc, isMobile, setLeftDrawerExpand]);

    useEffect(() => {
        if (!isMobile && addHeatpumpWaterHeater) {
            setLeftDrawerExpand(true);
        }
    }, [addHeatpumpWaterHeater, isMobile, setLeftDrawerExpand]);

    // Auto-close drawer if there is nothing to display.
    useEffect(() => {
        if (
            !hasEv &&
            !hasOtherScenarios &&
            !addInductionStove &&
            !addCleanHeatAndAc &&
            !addHeatpumpWaterHeater
        ) {
        }
    }, [
        hasEv,
        hasOtherScenarios,
        addInductionStove,
        addCleanHeatAndAc,
        addHeatpumpWaterHeater,
        setLeftDrawerExpand,
    ]);

    return (
        <div>
            {basicInfoExpand || (
                <div
                    className={styles.expandToggle}
                    onClick={() => setLeftDrawerExpand((v) => !v)}
                >
                    <IconChevronRight color="white" width={40} height={40} />
                </div>
            )}
            <Transition
                mounted={leftDrawerExpand}
                transition="slide-right"
                duration={250}
                timingFunction="ease"
                keepMounted
            >
                {(transitionStyle) => (
                    <div className={styles.drawer} style={transitionStyle}>
                        <IconChevronLeft
                            style={{ cursor: 'pointer' }}
                            onClick={() => setLeftDrawerExpand(false)}
                            color="white"
                            width={40}
                            height={40}
                        />

                        <div>
                            {!hasEv &&
                            !hasOtherScenarios &&
                            !addInductionStove &&
                            !addCleanHeatAndAc &&
                            !addHeatpumpWaterHeater ? (
                                <div className={styles.emptyStateMessage}>
                                    <div className={styles.emptyStateTitle}>
                                        No household consumption characteristics
                                        added.
                                    </div>
                                    <div className={styles.emptyStateText}>
                                        To include it, please select the cards
                                        on the right of the screen.
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {addCleanHeatAndAc && (
                                        <DrawerRow
                                            label="Clean Heat & AC"
                                            kWh={usageResult?.hvac ?? 0}
                                            onClickToRemove={() => {
                                                clearCleanHeatAndAc();
                                                ssrTrack(
                                                    'clickClearCleanHeatAndAc'
                                                );
                                            }}
                                            loading={loading}
                                        />
                                    )}

                                    {/* Display household size modification impact if it's the only change */}
                                    {usageResult?.hhSizeModificationImpactOnly ===
                                        true && (
                                        <DrawerRow
                                            label="Household Size Changes"
                                            kWh={usageResult?.home ?? 0}
                                            onClickToRemove={() => {
                                                setAddPeople('');
                                                ssrTrack(
                                                    'clickClearHouseholdSizeChanges'
                                                );
                                            }}
                                            loading={loading}
                                        />
                                    )}

                                    {(usageResult?.hvac_comfort ?? 0) > 0 && (
                                        <DrawerRow
                                            label="HVAC Usage"
                                            kWh={usageResult?.hvac_comfort ?? 0}
                                            onClickToRemove={() => {
                                                setAddHvacUse(false);
                                                ssrTrack(
                                                    'clickClearHVACComfort'
                                                );
                                            }}
                                            loading={loading}
                                        />
                                    )}

                                    {/* Display HVAC modification if present and not part of other changes */}
                                    {usageResult?.hvacModificationPresent &&
                                        !addCleanHeatAndAc && (
                                            <DrawerRow
                                                label="HVAC Modification"
                                                kWh={usageResult?.hvac ?? 0}
                                                onClickToRemove={() => {
                                                    clearCleanHeatAndAc();
                                                    ssrTrack(
                                                        'clickClearHVACModification'
                                                    );
                                                }}
                                                loading={loading}
                                            />
                                        )}

                                    {addHeatpumpWaterHeater && (
                                        <DrawerRow
                                            label="Heat Pump Water Heater"
                                            kWh={usageResult?.hpwh ?? 0}
                                            onClickToRemove={() => {
                                                clearHeatpumpWaterHeater();
                                                ssrTrack(
                                                    'clickClearHeatpumpWaterHeater'
                                                );
                                            }}
                                            loading={loading}
                                        />
                                    )}

                                    {addInductionStove && (
                                        <DrawerRow
                                            label="Induction Stove"
                                            kWh={usageResult?.stove ?? 0}
                                            onClickToRemove={() => {
                                                clearInductionStove();
                                                ssrTrack(
                                                    'clickClearInductionStove'
                                                );
                                            }}
                                            loading={loading}
                                        />
                                    )}
                                    {hasEv &&
                                        (validEvs.length === 1 ? (
                                            <DrawerRow
                                                label="Electric Car"
                                                kWh={usageResult?.ev_total ?? 0}
                                                onClickToRemove={() => {
                                                    clearEv();
                                                    ssrTrack('clickClearEv');
                                                }}
                                                loading={loading}
                                            />
                                        ) : (
                                            <>
                                                {validEvs.map((ev, index) => (
                                                    <DrawerRow
                                                        key={index}
                                                        label={`Electric Car ${index + 1}`}
                                                        kWh={
                                                            usageResult?.evs[
                                                                index
                                                            ] ?? 0
                                                        }
                                                        onClickToRemove={() => {
                                                            // Remove this specific EV
                                                            const newEvs = [
                                                                ...evs,
                                                            ];
                                                            newEvs.splice(
                                                                index,
                                                                1
                                                            );
                                                            setEvs(newEvs);
                                                            setNumEvs(
                                                                newEvs.length
                                                            );
                                                            ssrTrack(
                                                                `clickClearEv${index + 1}`
                                                            );
                                                        }}
                                                        loading={loading}
                                                    />
                                                ))}
                                            </>
                                        ))}
                                </>
                            )}
                        </div>

                        {/* Display notes at the bottom if present */}

                        {/* Display notes at the bottom if present */}
                        <div>
                            {usageResult?.notes?.hhSizeChangeNote && (
                                <div className={styles.notesSection}>
                                    <div className={styles.noteItem}>
                                        {usageResult.notes.hhSizeChangeNote}
                                    </div>
                                </div>
                            )}

                            {usageResult?.notes?.hvacPresenceNote && (
                                <div className={styles.notesSection}>
                                    <div className={styles.noteItem}>
                                        {usageResult.notes.hvacPresenceNote}
                                    </div>
                                </div>
                            )}
                        </div>

                        {!hasEv &&
                        !hasOtherScenarios &&
                        !addInductionStove &&
                        !addCleanHeatAndAc &&
                        !addHeatpumpWaterHeater ? null : (
                            <div
                                className={styles.clearButton}
                                onClick={() => {
                                    clearAll();
                                    ssrTrack('clickClearAll');
                                }}
                            >
                                Clear All
                            </div>
                        )}
                    </div>
                )}
            </Transition>
        </div>
    );
};

const DrawerRow: React.FC<{
    label: string;
    kWh: number;
    onClickToRemove: () => void;
    loading: boolean;
}> = ({ label, kWh, onClickToRemove, loading }) => {
    return (
        <div className={styles.drawerRow}>
            <IconCircleMinus
                className={styles.rowMinusButton}
                color="#D0CCC3"
                width={36}
                height={36}
                onClick={onClickToRemove}
            />
            <div className={styles.rowLabel}>{label}</div>
            <div className={styles.rowEnergy}>
                {loading ? (
                    <>
                        <Loader size={16} color="#a5c9ff" /> &nbsp;
                    </>
                ) : (
                    `${getSign(kWh)}${getDisplayNumber(kWh)} kWh`
                )}
            </div>
        </div>
    );
};
